import { Box, Stack, Text, Anchor } from '@atlaskit/primitives/compiled';
import { AppSwitcher } from '@atlaskit/navigation-system';
import { Popup } from '@atlaskit/popup';
import React, { useCallback, useState } from 'react';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { IconTile } from '@atlaskit/icon';
import { useVisibleApps } from '../../hooks/use-visible-apps';
import { useAnalyticsEvents } from '../../../common/analytics/generated/use-analytics-events';

const contentStyles = cssMap({
	root: {
		paddingInlineStart: token('space.100'),
		paddingBlockStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		paddingBlockEnd: token('space.100'),
		width: '343px', // Same as app switcher in other Atlassian apps
	},
	link: {
		textDecoration: 'none',

		'&:visited': {
			textDecoration: 'none',
		},
		'&:hover': {
			textDecoration: 'none',
		},
	},
	item: {
		paddingInline: token('space.100'),
		paddingBlock: token('space.100'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		borderRadius: token('radius.medium'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	emptyState: {
		paddingInline: token('space.200'),
		paddingBlock: token('space.300'),
		textAlign: 'center',
	},
});

const TwgAppSwitcherOptions = () => {
	const { apps, loading } = useVisibleApps();

	if (loading) {
		return (
			<Box xcss={contentStyles.emptyState}>
				<Text size="medium" color="color.text.subtlest">
					Loading apps...
				</Text>
			</Box>
		);
	}

	if (apps.length === 0) {
		return (
			<Box xcss={contentStyles.emptyState}>
				<Text size="medium" color="color.text.subtlest">
					No apps available
				</Text>
			</Box>
		);
	}

	return (
		<Stack>
			{apps.map((app, index) => {
				const { name, link, icon: Icon } = app;
				return (
					<Anchor key={index} href={link} xcss={contentStyles.link}>
						<Box xcss={contentStyles.item}>
							<IconTile
								icon={(props) => <Icon {...props} size="small" color={token('color.icon')} />}
								label=""
								size="medium"
								appearance="gray"
							/>
							<Text size="medium">{name}</Text>
						</Box>
					</Anchor>
				);
			})}
		</Stack>
	);
};

export const TwgAppSwitcher = (): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const { fireEvent } = useAnalyticsEvents();

	const handleSwitcherClick = useCallback(() => {
		fireEvent('ui.button.clicked.gcNavSwitcherClick', {
			action: isOpen ? 'close' : 'open',
		});
		setIsOpen(!isOpen);
	}, [fireEvent, isOpen]);

	return (
		<Popup
			shouldRenderToParent
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-start"
			content={() => (
				<Box xcss={contentStyles.root}>
					<TwgAppSwitcherOptions></TwgAppSwitcherOptions>
				</Box>
			)}
			trigger={(triggerProps) => (
				<AppSwitcher
					label="App Switcher"
					{...triggerProps}
					isSelected={isOpen}
					onClick={handleSwitcherClick}
				/>
			)}
		/>
	);
};
